import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, PieChart, Pie, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Cell, LabelList,
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '../components/ui/chart';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { securityService } from '../services/securityService';
import { API_URL } from '../config/apiConfig';
import { calculatePurposePercentages, StayPurposeInput } from '../utils/stayPurposeUtils';
import {
  NationalityData,
  OccupancyData,
  StayPurposeData,
} from '../types/security';

interface Statistics {
  totalGuests: number;
  activeGuests: number;
  totalHotels: number;
  activeAlerts: number;
  highRiskGuests: number;
  mediumRiskGuests: number;
  lowRiskGuests: number;
  todayCheckIns: number;
  todayCheckOuts: number;
  averageSecurityScore: number;
  topNationalities: Array<{ nationality: string; count: number }>;
  topHotels: Array<{ hotelName: string; guestCount: number }>;
  alertsByType: Array<{ type: string; count: number }>;
  monthlyTrends: Array<{ month: string; checkIns: number; alerts: number }>;
}

const NATIONALITY_COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))', 'hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];
const RISK_COLORS = { douce: 'hsl(var(--chart-2))', forte: 'hsl(var(--chart-3))', rouge: 'hsl(var(--chart-5))' };
const PURPOSE_COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-4))', 'hsl(var(--chart-3))', 'hsl(var(--chart-2))', 'hsl(var(--chart-5))', 'hsl(var(--chart-1))'];

const trendsChartConfig = {
  checkIns: { label: 'Check-ins', color: 'hsl(var(--chart-2))' },
  alerts: { label: 'Alertes', color: 'hsl(var(--chart-5))' },
} satisfies ChartConfig;

const occupancyChartConfig = {
  taux: { label: 'Occupation', color: 'hsl(var(--chart-4))' },
} satisfies ChartConfig;

const purposeChartConfig = {
  count: { label: 'Nombre', color: 'hsl(var(--chart-1))' },
} satisfies ChartConfig;

function StatisticsPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year'>('month');
  const [nationalities, setNationalities] = useState<NationalityData[]>([]);
  const [occupancy, setOccupancy] = useState<OccupancyData | null>(null);
  const [stayPurposes, setStayPurposes] = useState<StayPurposeData[]>([]);

  useEffect(() => {
    fetchAllData();
  }, [timeRange]);

  const getMockStatistics = (): Statistics => ({
    totalGuests: 1247,
    activeGuests: 342,
    totalHotels: 45,
    activeAlerts: 12,
    highRiskGuests: 23,
    mediumRiskGuests: 89,
    lowRiskGuests: 230,
    todayCheckIns: 18,
    todayCheckOuts: 15,
    averageSecurityScore: 72,
    topNationalities: [
      { nationality: 'Française', count: 234 },
      { nationality: 'Belge', count: 189 },
      { nationality: 'Américaine', count: 156 },
      { nationality: 'Britannique', count: 123 },
      { nationality: 'Chinoise', count: 98 },
    ],
    topHotels: [
      { hotelName: 'Pullman Kinshasa Grand Hotel', guestCount: 145 },
      { hotelName: 'Memling Hotel', guestCount: 132 },
      { hotelName: 'Fleuve Congo Hotel', guestCount: 98 },
      { hotelName: 'Sultani Hotel', guestCount: 87 },
      { hotelName: 'Béatrice Hotel', guestCount: 76 },
    ],
    alertsByType: [
      { type: 'Watchlist Match', count: 5 },
      { type: 'Document Anomaly', count: 8 },
      { type: 'Overstay', count: 12 },
      { type: 'Duplicate Entry', count: 3 },
    ],
    monthlyTrends: [
      { month: 'Jan', checkIns: 234, alerts: 12 },
      { month: 'Fév', checkIns: 289, alerts: 15 },
      { month: 'Mar', checkIns: 312, alerts: 18 },
      { month: 'Avr', checkIns: 298, alerts: 14 },
      { month: 'Mai', checkIns: 345, alerts: 20 },
      { month: 'Jun', checkIns: 367, alerts: 16 },
    ],
  });

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch main statistics
      let stats: Statistics;
      try {
        const response = await fetch(`${API_URL}/api/v1/dashboard/overview?range=${timeRange}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        stats = response.ok ? await response.json() : getMockStatistics();
      } catch {
        stats = getMockStatistics();
      }
      setStatistics(stats);

      // Fetch nationality breakdown via securityService
      try {
        const natData = await securityService.getNationalityBreakdown(timeRange);
        setNationalities(natData);
      } catch {
        setNationalities(stats.topNationalities);
      }

      // Fetch occupancy rates via securityService
      try {
        const occData = await securityService.getOccupancyRates();
        setOccupancy(occData);
      } catch {
        setOccupancy(null);
      }

      // Fetch stay purposes via securityService stats
      try {
        const svcStats = await securityService.getStats();
        if (svcStats?.stayPurposes && Array.isArray(svcStats.stayPurposes)) {
          const input: StayPurposeInput[] = svcStats.stayPurposes;
          setStayPurposes(calculatePurposePercentages(input));
        } else {
          setStayPurposes(calculatePurposePercentages([
            { purpose: 'Tourisme', count: 450 },
            { purpose: 'Affaires', count: 320 },
            { purpose: 'Transit', count: 180 },
            { purpose: 'Autre', count: 97 },
          ]));
        }
      } catch {
        setStayPurposes(calculatePurposePercentages([
          { purpose: 'Tourisme', count: 450 },
          { purpose: 'Affaires', count: 320 },
          { purpose: 'Transit', count: 180 },
          { purpose: 'Autre', count: 97 },
        ]));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'xlsx' | 'pdf') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/dashboard/overview/export?format=${format}&range=${timeRange}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `statistics_${timeRange}_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting statistics:', error);
      alert("Erreur lors de l'export des statistiques");
    }
  };

  /** Map severity-based risk counts to the 3-level system (douce/forte/rouge) */
  const getRiskDistribution = () => {
    if (!statistics) return [];
    return [
      { name: 'Douce', value: statistics.lowRiskGuests, color: RISK_COLORS.douce },
      { name: 'Forte', value: statistics.mediumRiskGuests, color: RISK_COLORS.forte },
      { name: 'Rouge', value: statistics.highRiskGuests, color: RISK_COLORS.rouge },
    ];
  };

  /** Build occupancy bar chart data from API or top hotels fallback */
  const getOccupancyChartData = () => {
    if (occupancy?.byHotel?.length) {
      return occupancy.byHotel.slice(0, 10).map(h => ({
        name: h.hotelName.length > 20 ? h.hotelName.substring(0, 18) + '…' : h.hotelName,
        taux: h.occupancyRate,
      }));
    }
    if (!statistics) return [];
    const maxGuests = statistics.topHotels[0]?.guestCount || 1;
    return statistics.topHotels.map(h => ({
      name: h.hotelName.length > 20 ? h.hotelName.substring(0, 18) + '…' : h.hotelName,
      taux: Math.round((h.guestCount / maxGuests) * 100),
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl">Chargement des statistiques...</div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl text-red-600">Erreur lors du chargement des statistiques</div>
      </div>
    );
  }

  const riskData = getRiskDistribution();
  const occupancyData = getOccupancyChartData();
  const nationalityData = nationalities.length > 0 ? nationalities : statistics.topNationalities;

  const nationalityChartConfig = nationalityData.reduce((acc, item, index) => {
    const key = 'nationality' in item ? item.nationality : `item-${index}`;
    acc[key] = {
      label: key,
      color: NATIONALITY_COLORS[index % NATIONALITY_COLORS.length],
    };
    return acc;
  }, { count: { label: 'Invités', color: 'hsl(var(--chart-1))' } } as ChartConfig);

  const riskChartConfig = riskData.reduce((acc, item) => {
    acc[item.name] = { label: item.name, color: item.color };
    return acc;
  }, {} as ChartConfig);

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">📊 Statistiques</h1>
            <p className="text-gray-600">Tableaux de bord et rapports analytiques</p>
          </div>
          <div className="flex gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as 'week' | 'month' | 'year')}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="year">Cette année</option>
            </select>
            <button onClick={() => handleExport('csv')} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">Export CSV</button>
            <button onClick={() => handleExport('xlsx')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">Export Excel</button>
            <button onClick={() => handleExport('pdf')} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition">Export PDF</button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Invités</p>
                <p className="text-3xl font-bold text-gray-900">{statistics.totalGuests}</p>
                <p className="text-sm text-green-600 mt-1">{statistics.activeGuests} actifs</p>
              </div>
              <div className="text-blue-600">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Hôtels</p>
                <p className="text-3xl font-bold text-gray-900">{statistics.totalHotels}</p>
                <p className="text-sm text-gray-500 mt-1">Enregistrés</p>
              </div>
              <div className="text-purple-600">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Alertes Actives</p>
                <p className="text-3xl font-bold text-red-600">{statistics.activeAlerts}</p>
                <p className="text-sm text-gray-500 mt-1">{statistics.highRiskGuests} risque élevé</p>
              </div>
              <div className="text-red-600">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Score Moyen</p>
                <p className="text-3xl font-bold text-gray-900">{statistics.averageSecurityScore}/100</p>
                <p className="text-sm text-gray-500 mt-1">Sécurité</p>
              </div>
              <div className="text-green-600">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
              </div>
            </div>
          </div>
        </div>

        {/* Today's Activity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Activité du Jour</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Check-ins</span>
                <span className="text-2xl font-bold text-green-600">{statistics.todayCheckIns}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Check-outs</span>
                <span className="text-2xl font-bold text-blue-600">{statistics.todayCheckOuts}</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Alertes par Type</h3>
            <div className="grid grid-cols-2 gap-3">
              {statistics.alertsByType.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">{item.type}</p>
                  <p className="text-xl font-bold text-gray-900">{item.count}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Monthly Trends LineChart (Req 5.1) */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg">Tendances Mensuelles</CardTitle>
          </CardHeader>
          <CardContent>
            {statistics.monthlyTrends.length > 0 ? (
              <ChartContainer config={trendsChartConfig} className="h-[300px] w-full">
                <LineChart data={statistics.monthlyTrends}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} />
                  <YAxis tickLine={false} axisLine={false} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Line type="monotone" dataKey="checkIns" stroke="var(--color-checkIns)" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="alerts" stroke="var(--color-alerts)" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ChartContainer>
            ) : (
              <p className="text-gray-500 text-center py-8">Pas de données</p>
            )}
          </CardContent>
        </Card>

        {/* Nationalities PieChart (Req 5.2) + Risk Distribution PieChart (Req 5.4) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Distribution des Nationalités</CardTitle>
            </CardHeader>
            <CardContent>
              {nationalityData.length > 0 ? (
                <ChartContainer config={nationalityChartConfig} className="h-[300px] w-full">
                  <PieChart>
                    <ChartTooltip content={<ChartTooltipContent nameKey="nationality" />} />
                    <Pie
                      data={nationalityData}
                      dataKey="count"
                      nameKey="nationality"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      label={({ name, percent }: { name?: string; percent?: number }) => `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                    >
                      {nationalityData.map((_entry, index) => (
                        <Cell key={`nat-${index}`} fill={NATIONALITY_COLORS[index % NATIONALITY_COLORS.length]} />
                      ))}
                    </Pie>
                    <ChartLegend content={<ChartLegendContent nameKey="nationality" />} />
                  </PieChart>
                </ChartContainer>
              ) : (
                <p className="text-gray-500 text-center py-8">Pas de données</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Distribution des Risques (Douce / Forte / Rouge)</CardTitle>
            </CardHeader>
            <CardContent>
              {riskData.some(d => d.value > 0) ? (
                <ChartContainer config={riskChartConfig} className="h-[300px] w-full">
                  <PieChart>
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Pie
                      data={riskData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      label={({ name, percent }: { name?: string; percent?: number }) => `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                    >
                      {riskData.map((entry, index) => (
                        <Cell key={`risk-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <ChartLegend content={<ChartLegendContent />} />
                  </PieChart>
                </ChartContainer>
              ) : (
                <p className="text-gray-500 text-center py-8">Pas de données</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Occupancy BarChart (Req 5.3) + Stay Purposes BarChart (Req 5.5, 4.1, 4.2) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Taux d'Occupation par Hôtel</CardTitle>
            </CardHeader>
            <CardContent>
              {occupancyData.length > 0 ? (
                <ChartContainer config={occupancyChartConfig} className="h-[300px] w-full">
                  <BarChart data={occupancyData} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} unit="%" tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                    <ChartTooltip content={<ChartTooltipContent formatter={(value) => [`${value}%`, 'Occupation']} />} />
                    <Bar dataKey="taux" fill="var(--color-taux)" radius={[0, 4, 4, 0]}>
                      <LabelList dataKey="taux" position="right" formatter={(v: number) => `${v}%`} className="fill-muted-foreground text-xs" />
                    </Bar>
                  </BarChart>
                </ChartContainer>
              ) : (
                <p className="text-gray-500 text-center py-8">Pas de données</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Motifs de Séjour</CardTitle>
            </CardHeader>
            <CardContent>
              {stayPurposes.length > 0 ? (
                <>
                  <ChartContainer config={purposeChartConfig} className="h-[250px] w-full">
                    <BarChart data={stayPurposes}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="purpose" tickLine={false} axisLine={false} />
                      <YAxis tickLine={false} axisLine={false} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {stayPurposes.map((_entry, index) => (
                          <Cell key={`purpose-${index}`} fill={PURPOSE_COLORS[index % PURPOSE_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ChartContainer>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {stayPurposes.map((p, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: PURPOSE_COLORS[i % PURPOSE_COLORS.length] }}></span>
                        <span className="text-gray-700">{p.purpose}: {p.count} ({p.percentage}%)</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Links */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/guests" className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:bg-blue-100 transition">
            <h4 className="font-semibold text-blue-900 mb-1">Voir tous les invités</h4>
            <p className="text-sm text-blue-700">Accéder à la liste complète</p>
          </Link>
          <Link to="/alerts" className="bg-red-50 border border-red-200 rounded-lg p-4 hover:bg-red-100 transition">
            <h4 className="font-semibold text-red-900 mb-1">Gérer les alertes</h4>
            <p className="text-sm text-red-700">Consulter les alertes actives</p>
          </Link>
          <Link to="/operational" className="bg-green-50 border border-green-200 rounded-lg p-4 hover:bg-green-100 transition">
            <h4 className="font-semibold text-green-900 mb-1">Dashboard opérationnel</h4>
            <p className="text-sm text-green-700">Vue d'ensemble en temps réel</p>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default StatisticsPage;
